import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  inject
} from '@angular/core';

/**
 * Publishes the live pixel height of the global banner stack into the
 * `--vex-global-banner-height` CSS custom property on the document root.
 *
 * The fixed toolbar (`position: fixed; top: 0`) ignores any banner that
 * precedes it in document flow, so without this it would render *under* the
 * banners and be hidden by them — the long-standing "le bandeau cache la top
 * barre" bug. The layout reads this variable to offset the toolbar's `top` and
 * the content's `margin-top`, so the banner stack pushes the whole chrome down
 * instead of overlapping it. When no banner is shown the host collapses to 0px
 * and the variable is 0 — the layout is then identical to before.
 */
@Directive({
  selector: '[appGlobalBannerOffset]'
})
export class GlobalBannerOffsetDirective implements OnDestroy {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);
  private observer?: ResizeObserver;

  private static readonly VAR = '--vex-global-banner-height';

  constructor() {
    // Measurement is pure layout work — keep it out of Angular's zone so the
    // ResizeObserver callback never triggers extra change-detection cycles.
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver === 'undefined') {
        // SSR / very old browser: leave the variable at its 0px default.
        return;
      }
      this.observer = new ResizeObserver(() => this.publishHeight());
      this.observer.observe(this.host.nativeElement);
      this.publishHeight();
    });
  }

  private publishHeight(): void {
    const height = Math.round(this.host.nativeElement.getBoundingClientRect().height);
    document.documentElement.style.setProperty(
      GlobalBannerOffsetDirective.VAR,
      `${height}px`
    );
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    document.documentElement.style.setProperty(
      GlobalBannerOffsetDirective.VAR,
      '0px'
    );
  }
}
